import { useInfiniteQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { Controls, ControlsButton } from '@/components/Controls';
import FullScreenButton from '@/components/FullScreenButton';
import InfiniteLoader from '@/components/InfiniteLoader';
import { PageHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import {
  ExtraLargeTh,
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from '@/components/Table';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import useHasPermission from '@/hooks/useHasPermission';
import {
  InvoiceResource,
  PaginatedResponse,
  SortState,
} from '@/types/resources';
import { api } from '@/utils/api';
import {
  getInvoiceSourceByLabel,
  getPaymentMethodByLabel,
} from '@/utils/data/payments';
import { InvoiceStatus, SortDirection } from '@/utils/enums';
import { RootState } from '@/utils/store';
import { hexToRgba } from '@/utils/theme';

import { isInvoiceSelectable } from '../utils';
import InvoicesExportModal from './InvoicesExportModal';
import InvoicesFilter from './InvoicesFilter';
import InvoicesImportModal from './InvoicesImportModal';
import InvoicesSearchBar from './InvoicesSearchBar';
import InvoicesStatusDropdown from './InvoicesStatusDropdown';
import InvoicesTableRow from './InvoicesTableRow';

export enum Tabs {
  PreReceived = InvoiceStatus.PreReceived,
  Received = InvoiceStatus.Received,
  Paid = InvoiceStatus.Paid,
  All = -1,
}

// Each tab is its own URL, so a view can be linked to and the back button steps
// through tabs. Slugs rather than the enum's numbers, since those are status
// ids and would leak into the URL as `/general/invoices/3`. The matching routes
// are registered in App.tsx.
const INVOICES_PATH = '/general/invoices';

const TAB_SLUGS: Record<Tabs, string> = {
  [Tabs.PreReceived]: 'pre-received',
  [Tabs.Received]: 'received',
  [Tabs.Paid]: 'paid',
  [Tabs.All]: 'all',
};

const DEFAULT_TAB = Tabs.Received;

// Both unpaid tabs gate on the approval mark: an invoice can't be selected for
// export or moved to Paid until someone has approved it.
const APPROVAL_TABS = [Tabs.PreReceived, Tabs.Received];

// `/general/invoices` carries no slug and shows the default tab, so links to it
// (the dashboard tile, the receiver list) keep working untouched.
const getTabByPathname = (pathname: string) => {
  const slug = pathname.split('/').filter(Boolean).pop();

  return (
    Object.values(Tabs)
      .filter((tab): tab is Tabs => typeof tab === 'number')
      .find((tab) => TAB_SLUGS[tab] === slug) ?? DEFAULT_TAB
  );
};

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DESC,
  property: 'receivedDate',
};

const columns = [
  {
    title: 'Receiver',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'True Receiver',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Comments',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Freelancer Notes',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Debtor Bank',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Creditor Bank',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Creditor Bank Country',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Payment Type',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Department',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Company',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Invoice ID',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Finance Account Number',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Amount (€)',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Amount ($)',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Amount (£)',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Payment Purpose',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Received Date',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Paid Date',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Created By',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Last Edited',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Files',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Proforma Files',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Additional Files',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Status',
    toggable: true,
    defaultVisibility: true,
  },
];

const StyledTableHeader = styled(TableHeader)`
  position: relative;
`;

export const HeadingsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
`;

const StyledTable = styled(Table)<{
  $hasCheckboxTh: boolean;
  $canEditData: boolean;
}>`
  border-collapse: separate;
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    min-width: 120px;
    &:nth-child(1) {
      min-width: 162px;
      position: sticky;
      z-index: 1;
      padding-right: 24px;
      padding-left: 24px;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
    }
    ${({ $hasCheckboxTh }) =>
      $hasCheckboxTh &&
      css`
        &:nth-child(1) {
          width: 82px;
          min-width: 82px;
          padding-left: 24px;
          padding-right: 12px;
          position: sticky;
          z-index: 1;
          background-color: ${({ theme }) => theme.colors.white};
          left: 0;
          top: 0;
          bottom: 0;
        }
        &:nth-child(2) {
          min-width: 162px;
          position: sticky;
          z-index: 1;
          padding-right: 24px;
          background-color: ${({ theme }) => theme.colors.white};
          left: 82px;
          top: 0;
          bottom: 0;
        }
      `}
    &:last-child {
      min-width: 56px;
      min-width: 56px;
      position: relative;
      z-index: 1;
      padding-left: 16px;
      padding-right: 16px;
    }
  }
  td {
    padding-top: 20px;
    padding-bottom: 20px;
    &:last-child {
      ${({ $canEditData }) =>
        $canEditData &&
        css`
          position: sticky;
          border-left: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
          right: -1px;
          top: 0;
          bottom: 0;
          z-index: 1;
        `}
    }
  }
`;

const LargeSortableTh = styled(SortableTh)`
  min-width: 140px;
`;

export const CheckboxHolder = styled.div`
  position: relative;
  width: 16px;
`;

export const CheckboxLabel = styled.label`
  display: block;
  height: 16px;
  width: 16px;
  right: 10px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  background-color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purpleLighter};
  }
`;

export const Checkbox = styled.input<{ $shouldUncheck?: boolean }>`
  display: none;
  &:checked + ${CheckboxLabel} {
    background: ${({ theme }) => theme.colors.purple};
    border-color: transparent;
    &:after {
      content: '';
      position: absolute;
      left: 5px;
      top: 2px;
      width: 5px;
      height: 9px;
      border: solid ${({ theme }) => theme.colors.white};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      ${({ $shouldUncheck }) =>
        $shouldUncheck &&
        css`
          background-color: ${({ theme }) => theme.colors.white};
          border-radius: 20px;
          width: 10px;
          height: 2px;
          border: 0;
          border-width: 0;
          transform: rotate(0deg);
          margin: auto;
          left: 0;
          top: 0;
          bottom: 0;
          right: 0;
        `}
    }
  }
`;

const ButtonsHolder = styled.div`
  display: flex;
  margin-left: auto;
  gap: 8px;
`;

const StyledNavLink = styled(NavLink)`
  height: 54px;
  justify-content: center;
  transition: 0.2s ease-out all;
  display: inline-flex;
  align-items: center;
  padding: 0 40px;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  font-family: 'Manrope', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  ${({ theme }) => css`
    color: ${theme.colors.purple};
    border-color: ${theme.colors.purple};
    &:hover {
      color: ${theme.colors.white};
      border-color: ${theme.colors.purpleLighter};
      background: ${theme.colors.purpleLighter};
    }
    &:active {
      color: ${theme.colors.white};
      border-color: ${theme.colors.purple};
      background: ${theme.colors.purple};
    }
  `}
`;

const HeadingButtons = styled.div`
  display: flex;
  gap: 16px;
`;

export const StyledButton = styled(Button)`
  &:disabled {
    background: ${({ theme }) => hexToRgba(theme.colors.black, 0.05)};
  }
`;

const InvoicesTable = () => {
  const location = useLocation();
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);

  const [selectedBanks, setSelectedBanks] = useState<string[]>([]);

  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);

  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);

  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState<string[]>(
    [],
  );

  const [selectedCreators, setSelectedCreators] = useState<number[]>([]);

  const [selectedBankTypes, setSelectedBankTypes] = useState<string[]>([]);

  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  const [paidAtDateRange, setPaidAtDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({});

  const [selectedBooleanValues, setSelectedBooleanValues] = useState<{
    gpm: boolean;
    thousand: boolean;
    missingBank: boolean;
    proforma: boolean;
  }>({ gpm: false, thousand: false, missingBank: false, proforma: false });

  const [showExportModal, setShowExportModal] = useState(false);

  const [showImportModal, setShowImportModal] = useState(false);

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const navigate = useNavigate();

  const activeTab = getTabByPathname(location.pathname);

  const setActiveTab = (tab: Tabs) =>
    navigate(`${INVOICES_PATH}/${TAB_SLUGS[tab]}`);

  const requiresApproval = APPROVAL_TABS.includes(activeTab);

  const memberName =
    (location.state as { memberName?: string })?.memberName || '';

  const [search, setSearch] = useState<string>(memberName);

  const userCanManageData = useHasPermission('manage invoices');

  const userCanAddInvoices = useHasPermission('add invoices');

  // Rows always fill this column when it's shown, even with no actions of their
  // own (a paid invoice on the All tab for non-managers); a missing cell would
  // make the row's last cell, e.g. Additional Files, the sticky one.
  const showActionsColumn = userCanManageData || activeTab !== Tabs.Paid;

  const tableViewOptions = useSelector(
    (rootState: RootState) => rootState.options.tableViews['invoicesTable'],
  );

  const fetchInvoices = ({ pageParam = '/v1/payments/invoices' }) => {
    return api.get<never, PaginatedResponse<InvoiceResource>>(pageParam, {
      params: {
        ...(!!search && {
          'filter[search]': search,
        }),
        ...(sort.direction !== 'DEFAULT' && {
          sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
        }),
        ...(activeTab !== Tabs.All && {
          'filter[status]': activeTab,
        }),
        ...(selectedBanks && {
          'filter[debtor_bank]': selectedBanks,
        }),
        ...(selectedDepartments && {
          'filter[department_id]': selectedDepartments,
        }),
        ...(selectedCompanies && {
          'filter[company_id]': selectedCompanies,
        }),
        ...(selectedPaymentTypes && {
          'filter[payment_type]': selectedPaymentTypes,
        }),
        ...(selectedCreators && {
          'filter[user_id]': selectedCreators,
        }),
        ...(selectedBooleanValues?.gpm && {
          'filter[gpm]': selectedBooleanValues?.gpm,
        }),
        ...(selectedBooleanValues?.thousand && {
          'filter[thousand]': selectedBooleanValues?.thousand,
        }),
        ...(selectedBooleanValues?.missingBank && {
          'filter[missingBank]': selectedBooleanValues?.missingBank,
        }),
        ...(selectedBooleanValues?.proforma && {
          'filter[is_proforma]': 1,
        }),
        ...(!!selectedSources.length && {
          'filter[source]': selectedSources.map(getInvoiceSourceByLabel),
        }),
        ...(selectedBankTypes.length === 1 && {
          'filter[payment_method]': getPaymentMethodByLabel(
            selectedBankTypes[0],
          ),
        }),
        ...(paidAtDateRange.rangeStart && {
          'filter[paid_at_start]':
            paidAtDateRange.rangeStart.toFormat('yyyy-MM-dd'),
          'filter[paid_at_end]': (
            paidAtDateRange.rangeEnd ?? paidAtDateRange.rangeStart
          ).toFormat('yyyy-MM-dd'),
        }),
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<InvoiceResource>>(
    [
      'invoices_list',
      {
        sort,
        search,
        activeTab,
        selectedBanks,
        selectedDepartments,
        selectedCompanies,
        selectedPaymentTypes,
        selectedCreators,
        selectedBankTypes,
        selectedSources,
        selectedBooleanValues,
        paidAtDateRange,
      },
    ],
    fetchInvoices,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const invoices = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<InvoiceResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  const onCheckboxChange = () => {
    if (!!selectedInvoices.length) return setSelectedInvoices([]);

    return setSelectedInvoices(
      invoices
        .filter((invoice) => isInvoiceSelectable(invoice, requiresApproval))
        .map((invoice) => invoice.id),
    );
  };

  // Every tab route renders this same component, so state survives a tab
  // switch. Without this, a select-all on the All/Paid tab would carry
  // unapproved invoices over into the Received tab's export.
  useEffect(() => {
    setSelectedInvoices([]);
  }, [activeTab]);

  // Drop invoices that stopped being selectable after a refetch, e.g. one that
  // was un-approved while ticked.
  useEffect(() => {
    setSelectedInvoices((prev) => {
      const next = prev.filter((id) => {
        const invoice = invoices.find((inv) => inv.id === id);
        return !invoice || isInvoiceSelectable(invoice, requiresApproval);
      });
      return next.length === prev.length ? prev : next;
    });
  }, [invoices, requiresApproval]);

  return (
    <>
      <HeadingsRow>
        <PageHeading>Invoice List</PageHeading>
        <HeadingButtons>
          <StyledButton
            disabled={!!!selectedInvoices.length}
            onClick={() => setShowExportModal(true)}
          >
            Export as
          </StyledButton>
          {userCanManageData && (
            <StyledButton
              color="secondary"
              onClick={() => setShowImportModal(true)}
            >
              Import file
            </StyledButton>
          )}
          {userCanAddInvoices && (
            <StyledNavLink to="/general/invoices/create">
              Add invoice
            </StyledNavLink>
          )}
        </HeadingButtons>
      </HeadingsRow>
      <StyledTableHeader>
        <Controls>
          <ControlsButton
            active={activeTab === Tabs.PreReceived}
            onClick={() => setActiveTab(Tabs.PreReceived)}
          >
            Pre-received
          </ControlsButton>
          <ControlsButton
            active={activeTab === Tabs.Received}
            onClick={() => setActiveTab(Tabs.Received)}
          >
            Received
          </ControlsButton>
          <ControlsButton
            active={activeTab === Tabs.Paid}
            onClick={() => setActiveTab(Tabs.Paid)}
          >
            Paid
          </ControlsButton>
          <ControlsButton
            active={activeTab === Tabs.All}
            onClick={() => setActiveTab(Tabs.All)}
          >
            All
          </ControlsButton>
        </Controls>
        <ButtonsHolder>
          {userCanManageData && (
            <InvoicesStatusDropdown
              invoicesIds={selectedInvoices}
              global
              status={InvoiceStatus.Received}
              setSelectedInvoices={setSelectedInvoices}
            />
          )}
          <InvoicesFilter
            selectedBanks={selectedBanks}
            selectedDepartments={selectedDepartments}
            selectedCompanies={selectedCompanies}
            setSelectedBanks={setSelectedBanks}
            setSelectedDepartments={setSelectedDepartments}
            setSelectedCompanies={setSelectedCompanies}
            selectedPaymentTypes={selectedPaymentTypes}
            setSelectedPaymentTypes={setSelectedPaymentTypes}
            selectedCreators={selectedCreators}
            setSelectedCreators={setSelectedCreators}
            selectedBankTypes={selectedBankTypes}
            setSelectedBankTypes={setSelectedBankTypes}
            selectedSources={selectedSources}
            setSelectedSources={setSelectedSources}
            selectedBooleanValues={selectedBooleanValues}
            setSelectedBooleanValues={setSelectedBooleanValues}
            paidAtDateRange={paidAtDateRange}
            setPaidAtDateRange={setPaidAtDateRange}
          />
          <ViewOptionsModal columns={columns} tableKey="invoicesTable" />

          <FullScreenButton />
        </ButtonsHolder>
      </StyledTableHeader>
      <InvoicesSearchBar
        setValue={setSearch}
        placeholder="Search Receiver, Invoice Id or IBAN"
        initialValue={memberName}
      />
      <ScrollableTable>
        <StyledTable
          $hasCheckboxTh={userCanManageData}
          $canEditData={showActionsColumn}
        >
          <thead>
            <tr>
              {!!invoices?.length && userCanManageData && (
                <Th>
                  <CheckboxHolder>
                    <Checkbox
                      id="select-all-invoices"
                      name="invoices"
                      type="checkbox"
                      checked={!!selectedInvoices.length}
                      $shouldUncheck={!!selectedInvoices.length}
                      onChange={onCheckboxChange}
                    />
                    <CheckboxLabel htmlFor="select-all-invoices" />
                  </CheckboxHolder>
                </Th>
              )}
              <LargeSortableTh
                property="receiver"
                title="Receiver"
                setSort={setSort}
                sort={sort}
              />
              {tableViewOptions['True Receiver'] && <Th>True Receiver</Th>}
              {tableViewOptions.Comments && <Th>Comments</Th>}
              {tableViewOptions['Freelancer Notes'] && (
                <Th>Freelancer Notes</Th>
              )}
              {tableViewOptions['Debtor Bank'] && (
                <LargeSortableTh
                  property="debtor_bank"
                  title="Debtor Bank"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Creditor Bank'] && (
                <LargeSortableTh
                  property="bank"
                  title="Creditor Bank"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Creditor Bank Country'] && (
                <LargeSortableTh
                  property="bank_country"
                  title="Creditor Bank Country"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Payment Type'] && (
                <LargeSortableTh
                  property="payment_type"
                  title="Payment Type"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions.Department && (
                <LargeSortableTh
                  property="department"
                  title="Department"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions.Company && (
                <LargeSortableTh
                  property="company"
                  title="Company"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Invoice ID'] && <Th>Invoice ID</Th>}
              {tableViewOptions['Finance Account Number'] && (
                <ExtraLargeTh>Finance Account Number</ExtraLargeTh>
              )}
              {tableViewOptions['Amount (€)'] && <Th>Amount (€)</Th>}
              {tableViewOptions['Amount ($)'] && <Th>Amount ($)</Th>}
              {tableViewOptions['Amount (£)'] && <Th>Amount (£)</Th>}
              {tableViewOptions['Payment Purpose'] && (
                <SortableTh
                  property="paymentPurpose"
                  title="Payment Purpose"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Received Date'] && (
                <SortableTh
                  property="receivedDate"
                  title="Received Date"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Paid Date'] && (
                <SortableTh
                  property="paidDate"
                  title="Paid Date"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {tableViewOptions['Created By'] && <Th>Created by</Th>}
              {tableViewOptions['Last Edited'] && <Th>Last Edited</Th>}
              {tableViewOptions.Files && <Th>Files</Th>}
              {tableViewOptions['Proforma Files'] && <Th>Proforma Files</Th>}
              {tableViewOptions['Additional Files'] && (
                <Th>Additional Files</Th>
              )}
              {userCanManageData && tableViewOptions.Status && <Th>Status</Th>}
              {showActionsColumn && (
                <Th>
                  <SrOnly>Actions</SrOnly>
                </Th>
              )}
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!invoices?.length}
            >
              {invoices?.map((invoice) => (
                <InvoicesTableRow
                  key={invoice.id}
                  invoice={invoice}
                  selectedInvoices={selectedInvoices}
                  setSelectedInvoices={setSelectedInvoices}
                  canManageData={userCanManageData}
                  showActionsColumn={showActionsColumn}
                  requiresApproval={requiresApproval}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <AnimatePresence>
        {showExportModal && (
          <InvoicesExportModal
            setSelectedInvoices={() => setSelectedInvoices([])}
            invoicesIds={selectedInvoices}
            invoicesIdsToMakePaid={selectedInvoices.filter(
              (id) =>
                invoices?.find((inv) => inv.id === id)?.status !==
                InvoiceStatus.Paid,
            )}
            onClose={() => setShowExportModal(false)}
            selectedPaymentTypes={selectedPaymentTypes}
          />
        )}
        {showImportModal && (
          <InvoicesImportModal onClose={() => setShowImportModal(false)} />
        )}
      </AnimatePresence>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default InvoicesTable;
